import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../redux/store";
import baseUrl from "../../../api/baseUrl";
import BottomTab from "../../../components/pagination/BottomTab";
import DeleteDialog from "../../components/modal/delete/DeleteDialog";
import {
  setPatients,
  setPatientsCount,
} from "../../../redux/patients/patientsAction";
import { Circles } from "react-loader-spinner";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import PatientViewModal from "./components/PatientViewModal";
import SearchIcon from "@mui/icons-material/Search";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PhoneIcon from "@mui/icons-material/Phone";
import CallIcon from "@mui/icons-material/Call";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

interface Patient {
  regNo: string;
  fullName: string;
  address: string;
  nic: string;
  mobileNo: string;
  dob: string;
  hasid: boolean;
  new: boolean;
  _id: string;
}

// Mobile Card Component
const PatientCard: React.FC<{
  patient: Patient;
  onView: (id: string) => void;
  onEdit: (id: string, e: React.MouseEvent) => void;
  onAddTreatment: (id: string, e: React.MouseEvent) => void;
  onDelete: (id: string, e: React.MouseEvent) => void;
  canDelete?: Boolean | false;
  theme?: "light" | "dark";
}> = React.memo(
  ({ patient, onView, onEdit, onAddTreatment, onDelete, canDelete, theme = "light" }) => {
    const formatAddress = () => {
      if (typeof patient?.address === "object" && patient?.address !== null) {
        const addr = patient.address as any;
        return `${addr.street || ""} ${addr.no || ""}, ${addr.city || ""}, ${
          addr.district || ""
        }`.trim();
      }
      return patient?.address || "";
    };

    return (
      <div
        className={`rounded-2xl p-4 shadow-md border transition-colors ${
          patient?.new
            ? theme === "dark"
              ? "border-emerald-500/50 bg-emerald-900/20"
              : "border-emerald-300 bg-emerald-50"
            : theme === "dark"
            ? "bg-slate-800 border-slate-700"
            : "bg-white border-slate-100"
        }`}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm shadow-md ${
              theme === "dark"
                ? "bg-gradient-to-br from-indigo-500 to-indigo-600"
                : "bg-gradient-to-br from-indigo-400 to-indigo-500"
            }`}>
              {patient?.fullName?.charAt(0)?.toUpperCase()}
            </div>
            <div>
              <p className={`font-semibold ${theme === "dark" ? "text-slate-200" : "text-slate-800"}`}>
                {patient?.fullName}
              </p>
              <span className={`px-2 py-0.5 text-xs font-medium rounded-full border transition-colors ${
                theme === "dark"
                  ? "bg-indigo-900/30 text-indigo-300 border-indigo-700"
                  : "bg-indigo-50 text-indigo-600 border-indigo-100"
              }`}>
                {patient?.regNo}
              </span>
            </div>
          </div>
          {patient?.new && (
            <span className={`px-2 py-1 text-xs font-medium rounded-full transition-colors ${
              theme === "dark"
                ? "bg-emerald-900/30 text-emerald-300"
                : "bg-emerald-100 text-emerald-600"
            }`}>
              NEW
            </span>
          )}
        </div>

        {/* Details */}
        <div className="space-y-2 text-sm mb-4">
          <div className={`flex items-center gap-2 ${theme === "dark" ? "text-slate-300" : "text-slate-600"}`}>
            <CalendarTodayIcon sx={{ fontSize: 16 }} className={theme === "dark" ? "text-slate-400" : "text-slate-400"} />
            <span>{patient?.dob?.split("T")[0]}</span>
          </div>
          <div className={`flex items-center gap-2 ${theme === "dark" ? "text-slate-300" : "text-slate-600"}`}>
            <PhoneIcon sx={{ fontSize: 16 }} className={theme === "dark" ? "text-slate-400" : "text-slate-400"} />
            <span className="flex-1">{patient?.mobileNo}</span>
            {patient?.mobileNo && (
              <a
                href={`tel:${patient?.mobileNo}`}
                onClick={(e) => e.stopPropagation()}
                className={`p-1.5 rounded-lg transition-colors ${
                  theme === "dark"
                    ? "bg-emerald-900/30 text-emerald-300 hover:bg-emerald-900/50"
                    : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                }`}
                title="Make Call"
              >
                <CallIcon sx={{ fontSize: 16 }} />
              </a>
            )}
          </div>
          <div className={`flex items-start gap-2 ${theme === "dark" ? "text-slate-300" : "text-slate-600"}`}>
            <LocationOnIcon sx={{ fontSize: 16 }} className={theme === "dark" ? "text-slate-400" : "text-slate-400"} />
            <span className="line-clamp-2">{formatAddress()}</span>
          </div>
        </div>

        {/* Actions */}
        <div className={`flex items-center gap-2 pt-3 border-t transition-colors ${
          theme === "dark" ? "border-slate-700" : "border-slate-100"
        }`}>
          <button
            onClick={() => onView(patient?._id)}
            className={`flex-1 flex items-center justify-center gap-1 px-3 py-2 rounded-lg transition-colors text-sm font-medium ${
              theme === "dark"
                ? "bg-blue-900/30 text-blue-300 hover:bg-blue-900/50"
                : "bg-blue-50 text-blue-600 hover:bg-blue-100"
            }`}
          >
            <RemoveRedEyeIcon sx={{ fontSize: 18 }} />
            View
          </button>
          <button
            onClick={(e) => onEdit(patient?._id, e)}
            className={`p-2 rounded-lg transition-colors ${
              theme === "dark"
                ? "bg-amber-900/30 text-amber-300 hover:bg-amber-900/50"
                : "bg-amber-50 text-amber-600 hover:bg-amber-100"
            }`}
          >
            <BorderColorIcon sx={{ fontSize: 18 }} />
          </button>
          <button
            onClick={(e) => onAddTreatment(patient?._id, e)}
            className={`p-2 rounded-lg transition-colors ${
              theme === "dark"
                ? "bg-emerald-900/30 text-emerald-300 hover:bg-emerald-900/50"
                : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
            }`}
          >
            <MedicalInformationIcon sx={{ fontSize: 18 }} />
          </button>
          {patient?.hasid && canDelete && (
            <button
              onClick={(e) => onDelete(patient?._id, e)}
              className={`p-2 rounded-lg transition-colors ${
                theme === "dark"
                  ? "bg-red-900/30 text-red-400 hover:bg-red-900/50"
                  : "bg-red-50 text-red-500 hover:bg-red-100"
              }`}
            >
              <DeleteOutlineIcon sx={{ fontSize: 18 }} />
            </button>
          )}
        </div>
      </div>
    );
  }
);

// Desktop Table Row Component
const PatientRow: React.FC<{
  patient: Patient;
  onView: (id: string) => void;
  onEdit: (id: string, e: React.MouseEvent) => void;
  onAddTreatment: (id: string, e: React.MouseEvent) => void;
  onDelete: (id: string, e: React.MouseEvent) => void;
  canDelete?: Boolean | false;
  theme?: "light" | "dark";
}> = React.memo(
  ({ patient, onView, onEdit, onAddTreatment, onDelete, canDelete, theme = "light" }) => {
    const formatAddress = () => {
      if (typeof patient?.address === "object" && patient?.address !== null) {
        const addr = patient.address as any;
        return `${addr.street || ""} ${addr.no || ""}, ${addr.city || ""}, ${
          addr.district || ""
        }, ${addr.province || ""}, ${addr.country || ""}`.trim();
      }
      return patient?.address || "";
    };

    return (
      <tr
        className={`border-b transition-colors ${
          patient?.new
            ? theme === "dark"
              ? "bg-emerald-900/20 border-slate-700 hover:bg-emerald-900/30"
              : "bg-emerald-50 border-slate-100 hover:bg-emerald-50/80"
            : theme === "dark"
            ? "border-slate-700 hover:bg-slate-700/50"
            : "border-slate-100 hover:bg-slate-50/50"
        }`}
      >
        <td className="px-3 py-2">
          <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border transition-colors ${
            theme === "dark"
              ? "bg-indigo-900/30 text-indigo-300 border-indigo-700"
              : "bg-indigo-50 text-indigo-600 border-indigo-100"
          }`}>
            {patient?.regNo}
          </span>
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs shadow-md ${
              theme === "dark"
                ? "bg-gradient-to-br from-indigo-500 to-indigo-600"
                : "bg-gradient-to-br from-indigo-400 to-indigo-500"
            }`}>
              {patient?.fullName?.charAt(0)?.toUpperCase()}
            </div>
            <span className={`font-medium text-sm ${theme === "dark" ? "text-slate-200" : "text-slate-800"}`}>
              {patient?.fullName}
            </span>
          </div>
        </td>
        <td className={`px-3 py-2 text-xs ${theme === "dark" ? "text-slate-300" : "text-slate-600"}`}>
          {patient?.dob?.split("T")[0]}
        </td>
        <td className={`px-3 py-2 text-xs max-w-[180px] truncate ${theme === "dark" ? "text-slate-300" : "text-slate-600"}`}>
          {formatAddress()}
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center gap-2">
            <span className={`text-xs ${theme === "dark" ? "text-slate-300" : "text-slate-600"}`}>
              {patient?.mobileNo}
            </span>
            {patient?.mobileNo && (
              <a
                href={`tel:${patient?.mobileNo}`}
                onClick={(e) => e.stopPropagation()}
                className={`p-1 rounded-lg transition-colors ${
                  theme === "dark"
                    ? "bg-emerald-900/30 text-emerald-300 hover:bg-emerald-900/50"
                    : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                }`}
                title="Make Call"
              >
                <CallIcon sx={{ fontSize: 14 }} />
              </a>
            )}
          </div>
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center justify-end gap-1.5">
            <button
              onClick={() => onView(patient?._id)}
              className={`p-1.5 rounded-lg transition-colors ${
                theme === "dark"
                  ? "bg-blue-900/30 text-blue-300 hover:bg-blue-900/50"
                  : "bg-blue-50 text-blue-600 hover:bg-blue-100"
              }`}
              title="View"
            >
              <RemoveRedEyeIcon sx={{ fontSize: 16 }} />
            </button>
            <button
              onClick={(e) => onEdit(patient?._id, e)}
              className={`p-1.5 rounded-lg transition-colors ${
                theme === "dark"
                  ? "bg-amber-900/30 text-amber-300 hover:bg-amber-900/50"
                  : "bg-amber-50 text-amber-600 hover:bg-amber-100"
              }`}
              title="Edit"
            >
              <BorderColorIcon sx={{ fontSize: 16 }} />
            </button>
            <button
              onClick={(e) => onAddTreatment(patient?._id, e)}
              className={`p-1.5 rounded-lg transition-colors ${
                theme === "dark"
                  ? "bg-emerald-900/30 text-emerald-300 hover:bg-emerald-900/50"
                  : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
              }`}
              title="Add Treatment"
            >
              <MedicalInformationIcon sx={{ fontSize: 16 }} />
            </button>
            {patient?.hasid && canDelete && (
              <button
                onClick={(e) => onDelete(patient?._id, e)}
                className={`p-1.5 rounded-lg transition-colors ${
                  theme === "dark"
                    ? "bg-red-900/30 text-red-400 hover:bg-red-900/50"
                    : "bg-red-50 text-red-500 hover:bg-red-100"
                }`}
                title="Delete"
              >
                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  }
);

const ActivePatient: React.FC = () => {
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingtreatment, setLoadingtreatment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletemodal, setdeletemodal] = useState(false);
  const [canDelete, setcanDelete] = useState(false);
  const [deleteId, setdeleteId] = useState<string | null>(null);
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const patients = useSelector((state: RootState) => state.patients.patients);
  const patientscount = useSelector(
    (state: RootState) => state.patients.patientsCount
  );
  const [search, setsearch] = useState("");
  const [searchdob, setsearchdob] = useState("");
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewPatientData, setViewPatientData] = useState<any>(null);
  const [loadingPatient, setLoadingPatient] = useState(false);

  const handleClickGoViewPatient = useCallback(
    (id: string) => {
      setLoadingPatient(true);
      setViewModalOpen(true);
      baseUrl
        .get("patient/findById", {
          headers: {
            patientid: id,
          },
        })
        .then((response) => {
          setViewPatientData(response.data.data);
          setLoadingPatient(false);
        })
        .catch((error) => {
          console.error(error);
          setLoadingPatient(false);
        });
    },
    []
  );

  const closeViewModal = useCallback(() => {
    setViewModalOpen(false);
    setViewPatientData(null);
  }, []);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") as "light" | "dark";
    const htmlTheme = document.documentElement.getAttribute("data-theme") as "light" | "dark";
    
    if (savedTheme) {
      setTheme(savedTheme);
    } else if (htmlTheme) {
      setTheme(htmlTheme);
    }

    // Listen for theme changes
    const observer = new MutationObserver(() => {
      const currentTheme = document.documentElement.getAttribute("data-theme") as "light" | "dark";
      if (currentTheme) {
        setTheme(currentTheme);
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  const handleClickGoEditPatient = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      navigate(`/client/patients/edit-patients/${id}`);
    },
    [navigate]
  );

  const handleTreatmentClick = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setLoadingtreatment(true);
      baseUrl
        .post("treatment/create-treatment", { patientId: id })
        .then((response) => {
          setLoadingtreatment(false);
          navigate(
            `/client/treatment-diagnose/add-treatment/diagnose/${response.data.data._id}`
          );
        })
        .catch((error) => {
          console.error(error);
        });
    },
    [navigate]
  );

  const confirmDelete = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setdeleteId(id);
      setdeletemodal(true);
    },
    [setdeleteId, setdeletemodal]
  );

  const cancelDelete = useCallback(
    (id: string, e: React.MouseEvent) => {
      setdeletemodal(false);
    },
    [setdeletemodal]
  );

  const getData = useCallback(
    (searchTerm: string, pageNum: number) => {
      setLoading(true);
      setError(null);
      baseUrl
        .get("client/patient/find-all-patients", {
          headers: {
            status: true,
            page: pageNum,
            keyword: searchTerm,
          },
        })
        .then((response) => {
          setLoading(false);
          if (response.data.status === 204) {
            dispatch(setPatients([]));
            dispatch(setPatientsCount(0));
          } else {
            dispatch(setPatients(response.data.data));
            dispatch(setPatientsCount(response.data.count));
          }
        })
        .catch((error) => {
          setError(error.response?.data?.message || "Error fetching data");
          setLoading(false);
        });
    },
    [dispatch]
  );

  const handlePatientSearch = (e: any) => {
    setsearch(e.target.value);
    getData(e.target.value, page);
  };

  const handleDobSearch = (e: any) => {
    setsearchdob(e.target.value);
  };

  const onDobSearch = () => {
    getData(searchdob, page);
  };

  const handleDeleteClick = useCallback(() => {
    if (deleteId) {
      baseUrl
        .delete("patient/deleteLastPatientById", {
          headers: { patientid: deleteId },
        })
        .then((response) => {
          setdeletemodal(false);
          getData(search, page);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  }, [deleteId, page, getData, search]);

  useEffect(() => {
    if (patients?.length < 19) {
      getData("", page);
    }
  }, [page, getData]);

  const onPageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      getData(search, newPage);
    },
    [getData, search]
  );

  const canDeleteRow = useCallback(
    (e: boolean) => {
      setcanDelete(!e);
      getData(searchdob, page);
    },
    [getData, searchdob, page]
  );

  // Mobile Cards
  const mobileCards = useMemo(() => {
    return patients?.map((patient: any) => (
      <PatientCard
        key={patient._id}
        patient={patient}
        onView={handleClickGoViewPatient}
        onEdit={handleClickGoEditPatient}
        onAddTreatment={handleTreatmentClick}
        onDelete={confirmDelete}
        canDelete={canDelete}
        theme={theme}
      />
    ));
  }, [
    patients,
    handleClickGoViewPatient,
    handleClickGoEditPatient,
    handleTreatmentClick,
    confirmDelete,
    canDelete,
    theme,
  ]);

  // Desktop Table Rows
  const renderedTableRows = useMemo(() => {
    return patients?.map((patient: any) => (
      <PatientRow
        key={patient._id}
        patient={patient}
        onView={handleClickGoViewPatient}
        onEdit={handleClickGoEditPatient}
        onAddTreatment={handleTreatmentClick}
        onDelete={confirmDelete}
        canDelete={canDelete}
        theme={theme}
      />
    ));
  }, [
    patients,
    handleClickGoViewPatient,
    handleClickGoEditPatient,
    handleTreatmentClick,
    confirmDelete,
    canDelete,
    theme,
  ]);

  return (
    <div className="space-y-4">
      {/* Search and Controls */}
      <div className={`rounded-[8px] shadow-lg border p-3 transition-colors duration-300 ${
        theme === "dark" 
          ? "bg-slate-800 border-slate-700 shadow-slate-900/50" 
          : "bg-white border-slate-100 shadow-slate-200/50"
      }`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2.5">
          {/* Search Inputs */}
          <div className="flex flex-col sm:flex-row gap-2 flex-1">
            {/* Name Search */}
            <div className="relative flex-1 max-w-md">
              <SearchIcon
                className={`absolute left-3 top-1/2 -translate-y-1/2 z-10 transition-colors ${
                  theme === "dark" ? "text-slate-400" : "text-slate-400"
                }`}
                sx={{ fontSize: 18 }}
              />
              <input
                type="search"
                placeholder="Search patients..."
                onChange={handlePatientSearch}
                value={search}
                className={`w-full h-9 pl-10 pr-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all text-xs font-medium placeholder:text-slate-400 ${
                  theme === "dark"
                    ? "bg-slate-700 border-slate-600 text-slate-200 placeholder:text-slate-500 focus:bg-slate-600"
                    : "bg-slate-50 border-slate-200 text-slate-700 focus:bg-white"
                }`}
              />
            </div>

            {/* DOB Search */}
            <div className="flex gap-2">
              <div className="relative">
                <CalendarTodayIcon
                  className={`absolute left-3 top-1/2 -translate-y-1/2 z-10 transition-colors ${
                    theme === "dark" ? "text-slate-400" : "text-slate-400"
                  }`}
                  sx={{ fontSize: 16 }}
                />
                <input
                  type="date"
                  onChange={handleDobSearch}
                  value={searchdob}
                  className={`h-9 pl-10 pr-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all text-xs font-medium ${
                    theme === "dark"
                      ? "bg-slate-700 border-slate-600 text-slate-200 focus:bg-slate-600"
                      : "bg-slate-50 border-slate-200 text-slate-700 focus:bg-white"
                  }`}
                />
              </div>
              <button
                onClick={() => onDobSearch()}
                className={`h-9 px-4 rounded-lg font-semibold transition-all duration-300 flex items-center gap-1.5 shadow-sm hover:shadow-md active:scale-95 text-xs ${
                  theme === "dark"
                    ? "bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white"
                    : "bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white"
                }`}
              >
                <SearchIcon sx={{ fontSize: 16 }} />
                <span className="hidden sm:inline">Search</span>
              </button>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2 flex-wrap">
            {!canDelete ? (
              <button
                onClick={() => canDeleteRow(false)}
                className={`h-9 px-3.5 rounded-lg font-semibold transition-all duration-300 text-xs shadow-sm hover:shadow-md active:scale-95 ${
                  theme === "dark"
                    ? "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white"
                    : "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white"
                }`}
              >
                Enable Delete
              </button>
            ) : (
              <button
                onClick={() => canDeleteRow(true)}
                className={`h-9 px-3.5 rounded-lg font-semibold transition-all duration-300 text-xs shadow-sm hover:shadow-md active:scale-95 ${
                  theme === "dark"
                    ? "bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white"
                    : "bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white"
                }`}
              >
                Disable Delete
              </button>
            )}
            <div className={`flex items-center gap-1.5 h-9 px-3.5 rounded-lg border transition-colors ${
              theme === "dark"
                ? "bg-slate-700 border-slate-600"
                : "bg-slate-100 border-slate-200"
            }`}>
              <PeopleAltIcon className={theme === "dark" ? "text-indigo-400" : "text-indigo-500"} sx={{ fontSize: 18 }} />
              <span className={`font-bold text-xs ${
                theme === "dark" ? "text-slate-200" : "text-slate-800"
              }`}>{patientscount}</span>
              <span className={`text-xs ${
                theme === "dark" ? "text-slate-400" : "text-slate-500"
              }`}>Patients</span>
            </div>
            <button
              onClick={() => navigate("/client/patients/quick-add")}
              className={`h-9 px-3.5 rounded-lg font-semibold transition-all duration-300 flex items-center gap-1.5 shadow-sm hover:shadow-md active:scale-95 text-xs ${
                theme === "dark"
                  ? "bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white"
                  : "bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white"
              }`}
            >
              <PersonAddIcon sx={{ fontSize: 18 }} />
              <span className="hidden sm:inline">Add Patient</span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className={`rounded-2xl p-8 text-center transition-colors ${
            theme === "dark" ? "bg-slate-800" : "bg-white"
          }`}>
            <Circles height="40" width="40" color="#6366f1" ariaLabel="loading" />
          </div>
        ) : error ? (
          <div className={`rounded-2xl p-8 text-center text-red-500 transition-colors ${
            theme === "dark" ? "bg-slate-800" : "bg-white"
          }`}>
            {error}
          </div>
        ) : patients?.length === 0 ? (
          <div className={`rounded-2xl py-12 text-center shadow-md transition-colors ${
            theme === "dark" ? "bg-slate-800 border border-slate-700" : "bg-white"
          }`}>
            <PeopleAltIcon className={theme === "dark" ? "text-slate-500" : "text-slate-300"} sx={{ fontSize: 48 }} />
            <p className={theme === "dark" ? "text-slate-400 font-medium" : "text-slate-500 font-medium"}>No patients found</p>
          </div>
        ) : (
          mobileCards
        )}
      </div>

      {/* Desktop Table View */}
      <div className={`hidden md:block rounded-[8px] shadow-lg border overflow-hidden transition-colors ${
        theme === "dark"
          ? "bg-slate-800 border-slate-700 shadow-slate-900/50"
          : "bg-white border-slate-100 shadow-slate-200/50"
      }`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className={theme === "dark" ? "bg-slate-700/80" : "bg-slate-50/80"}>
                <th className={`px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider ${
                  theme === "dark" ? "text-slate-300" : "text-slate-500"
                }`}>
                  Reg No
                </th>
                <th className={`px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider ${
                  theme === "dark" ? "text-slate-300" : "text-slate-500"
                }`}>
                  Full Name
                </th>
                <th className={`px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider ${
                  theme === "dark" ? "text-slate-300" : "text-slate-500"
                }`}>
                  DOB
                </th>
                <th className={`px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider ${
                  theme === "dark" ? "text-slate-300" : "text-slate-500"
                }`}>
                  Address
                </th>
                <th className={`px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider ${
                  theme === "dark" ? "text-slate-300" : "text-slate-500"
                }`}>
                  Mobile
                </th>
                <th className={`px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider ${
                  theme === "dark" ? "text-slate-300" : "text-slate-500"
                }`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={theme === "dark" ? "divide-y divide-slate-700" : "divide-y divide-slate-100"}>
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <Circles height="40" width="40" color="#6366f1" ariaLabel="loading" />
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-red-500">
                    {error}
                  </td>
                </tr>
              ) : patients?.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <PeopleAltIcon
                      className={theme === "dark" ? "text-slate-500" : "text-slate-300"}
                      sx={{ fontSize: 48 }}
                    />
                    <p className={theme === "dark" ? "text-slate-400 font-medium" : "text-slate-500 font-medium"}>No patients found</p>
                  </td>
                </tr>
              ) : (
                renderedTableRows
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <BottomTab title="Patient" count={patientscount} onPageChange={onPageChange} />

      {/* Delete Modal */}
      {deletemodal && (
        <DeleteDialog
          onDelete={handleDeleteClick}
          onCancel={cancelDelete}
          title={"Are you sure you want to delete this patient's record?"}
          deletes="DELETE"
        />
      )}

      {/* Loading Overlay */}
      {loadingtreatment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Circles height="60" width="60" color="#6366f1" ariaLabel="loading" />
        </div>
      )}

      {/* Patient View Modal */}
      <PatientViewModal
        isOpen={viewModalOpen}
        onClose={closeViewModal}
        patientData={viewPatientData}
        loading={loadingPatient}
        theme={theme}
        onEdit={handleClickGoEditPatient}
        onAddTreatment={handleTreatmentClick}
        onDelete={confirmDelete}
        canDelete={canDelete}
      />
    </div>
  );
};

export default ActivePatient;
